/**
 * Expose Vite env on window.VITE_ENV for modules that read it at load time.
 * Import this module first in main.jsx (before productionLogger and App).
 *
 * Only the keys named explicitly below are copied.
 *
 * This used to assign `import.meta.env` wholesale, which put every VITE_
 * variable in the .env file onto `window` — readable from any browser console,
 * and materialised into the bundle whether or not a line of code referenced it.
 * That is how VITE_RAZORPAY_KEY_SECRET came to be served to every visitor of
 * the production site despite nothing in the application ever reading it.
 *
 * Adding a key here is a deliberate act: anything on this list is public, and
 * belongs to the class of values that are safe to hand to a browser — Firebase
 * web config, publishable payment identifiers, feature flags, public URLs. A
 * secret never goes on this list. It goes in Secret Manager and is used by the
 * API, the way GOOGLE_STREETVIEW_API_KEY is.
 */


declare global {
  interface Window {
    VITE_ENV: Partial<ImportMetaEnv> & {
      readonly PROD: boolean;
      readonly DEV: boolean;
      readonly MODE: string;
    };
  }
}

if (typeof window !== 'undefined') {
  /*
    Each key is referenced individually, and that is load-bearing.

    Vite replaces `import.meta.env.VITE_X` with a literal at build time, but
    only when the property is named. Touching the object as a whole — as
    `window.VITE_ENV = import.meta.env` did, and as a runtime filter over
    `const source = import.meta.env` still would — makes Vite emit the entire
    env object into the bundle, every VITE_ variable in the .env file included.
    Filtering afterwards is far too late: the values are already in the file.

    Verified by building and grepping the output for the secrets themselves.
  */
  const exposed: Record<string, unknown> = {
    BASE_URL: import.meta.env.BASE_URL,
    DEV: import.meta.env.DEV,
    MODE: import.meta.env.MODE,
    PROD: import.meta.env.PROD,
    VITE_FIREBASE_API_KEY: import.meta.env.VITE_FIREBASE_API_KEY,
    VITE_FIREBASE_APP_ID: import.meta.env.VITE_FIREBASE_APP_ID,
    VITE_FIREBASE_AUTH_DOMAIN: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
    VITE_FIREBASE_MEASUREMENT_ID: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
    VITE_FIREBASE_MESSAGING_SENDER_ID: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
    VITE_FIREBASE_PROJECT_ID: import.meta.env.VITE_FIREBASE_PROJECT_ID,
    VITE_FIREBASE_STORAGE_BUCKET: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
    NODE_ENV: import.meta.env.NODE_ENV,
    VITE_API_BASE_URL: import.meta.env.VITE_API_BASE_URL,
    VITE_API_PROXY_URL: import.meta.env.VITE_API_PROXY_URL,
    VITE_API_SAME_ORIGIN: import.meta.env.VITE_API_SAME_ORIGIN,
    VITE_ENABLE_LOGGING: import.meta.env.VITE_ENABLE_LOGGING,
    VITE_USE_FUNCTIONS_EMULATOR: import.meta.env.VITE_USE_FUNCTIONS_EMULATOR,
    // Public browser key; restrict it by HTTP referrer and API allow-list in Google Cloud.
    VITE_GOOGLE_MAPS_API_KEY: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    VITE_N8N_LOG_WEBHOOK_URL: import.meta.env.VITE_N8N_LOG_WEBHOOK_URL,
    VITE_N8N_WEBHOOK_URL: import.meta.env.VITE_N8N_WEBHOOK_URL,
    VITE_MESHY_API_BASE_URL: import.meta.env.VITE_MESHY_API_BASE_URL,
    VITE_CLOUDINARY_CLOAD_NAME: import.meta.env.VITE_CLOUDINARY_CLOAD_NAME,
    VITE_CLOUDINARY_CLOUD_NAME: import.meta.env.VITE_CLOUDINARY_CLOUD_NAME,
    VITE_CLOUDINARY_UPLOAD_PRESET: import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET,
    VITE_PADDLE_CLIENT_TOKEN: import.meta.env.VITE_PADDLE_CLIENT_TOKEN,
    VITE_PADDLE_ENTERPRISE_MONTHLY_PRICE_ID: import.meta.env.VITE_PADDLE_ENTERPRISE_MONTHLY_PRICE_ID,
    VITE_PADDLE_ENTERPRISE_YEARLY_PRICE_ID: import.meta.env.VITE_PADDLE_ENTERPRISE_YEARLY_PRICE_ID,
    VITE_PADDLE_ENVIRONMENT: import.meta.env.VITE_PADDLE_ENVIRONMENT,
    VITE_PADDLE_PRO_MONTHLY_PRICE_ID: import.meta.env.VITE_PADDLE_PRO_MONTHLY_PRICE_ID,
    VITE_PADDLE_PRO_YEARLY_PRICE_ID: import.meta.env.VITE_PADDLE_PRO_YEARLY_PRICE_ID,
    VITE_PADDLE_TEAM_MONTHLY_PRICE_ID: import.meta.env.VITE_PADDLE_TEAM_MONTHLY_PRICE_ID,
    VITE_PADDLE_TEAM_YEARLY_PRICE_ID: import.meta.env.VITE_PADDLE_TEAM_YEARLY_PRICE_ID,
    VITE_RAZORPAY_ENTERPRISE_MONTHLY_PLAN_ID: import.meta.env.VITE_RAZORPAY_ENTERPRISE_MONTHLY_PLAN_ID,
    VITE_RAZORPAY_ENTERPRISE_YEARLY_PLAN_ID: import.meta.env.VITE_RAZORPAY_ENTERPRISE_YEARLY_PLAN_ID,
    VITE_RAZORPAY_KEY_ID: import.meta.env.VITE_RAZORPAY_KEY_ID,
    VITE_RAZORPAY_PRO_MONTHLY_PLAN_ID: import.meta.env.VITE_RAZORPAY_PRO_MONTHLY_PLAN_ID,
    VITE_RAZORPAY_PRO_YEARLY_PLAN_ID: import.meta.env.VITE_RAZORPAY_PRO_YEARLY_PLAN_ID,
    VITE_RAZORPAY_TEAM_MONTHLY_PLAN_ID: import.meta.env.VITE_RAZORPAY_TEAM_MONTHLY_PLAN_ID,
    VITE_RAZORPAY_TEAM_YEARLY_PLAN_ID: import.meta.env.VITE_RAZORPAY_TEAM_YEARLY_PLAN_ID,
  };
  for (const key of Object.keys(exposed)) {
    if (exposed[key] === undefined) delete exposed[key];
  }
  window.VITE_ENV = exposed as Window['VITE_ENV'];
}

export {};
